import {
    Stack,
    TextField,
    PrimaryButton,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Text,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Dropdown,
    IDropdownOption,
    Checkbox,
    Icon,
} from '@fluentui/react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import {
    checkHashInStorage,
    StorageBlobInfo,
    transferBlobs,
    BlobTransferResult,
    TransferDirection,
    WorkLane,
} from '../../../../ABSM/api/ABSMApi';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';

interface ICheckHashInStorageProps {}

// Which side of a transfer the operator picked as the source. Destination is
// always the other one.
type TransferSource = 'cache' | 'cloudContainers';

// A row is eligible for transfer only when it can actually be copied FROM the
// chosen source, and (for the Cloud->Cache direction) is actually worth copying:
// - Source = Cache            -> push out: must exist in cache (existsInCache === true).
// - Source = Cloud Containers -> pull in: must exist in a container (exists === true)
//                                AND be confirmed absent from the cache (existsInCache === false).
//                                existsInCache === null/undefined means the cache was not probed
//                                (no cache account configured in this environment), so a
//                                Cloud->Cache transfer cannot succeed and the row is NOT eligible.
const isRowEligible = (
    item: StorageBlobInfo,
    source: TransferSource
): boolean =>
    source === 'cache'
        ? item.existsInCache === true
        : item.exists === true && item.existsInCache === false;

// Three-state status for a row under the Cloud Containers -> Cache direction.
// Drives the non-interactive status icon in the select column.
//  - 'willCache'     : in a container, not yet cached -> will be added to cache.
//  - 'alreadyCached' : in a container, already cached  -> skipped (no-op).
//  - 'notAvailable'  : not in any container            -> cannot be transferred.
type CloudToCacheStatus = 'willCache' | 'alreadyCached' | 'notAvailable';

const getCloudToCacheStatus = (item: StorageBlobInfo): CloudToCacheStatus => {
    if (item.exists !== true) {
        return 'notAvailable';
    }
    if (item.existsInCache === true) {
        return 'alreadyCached';
    }
    if (item.existsInCache === false) {
        return 'willCache';
    }
    // existsInCache null/undefined -> cache not configured/probed in this environment,
    // so the blob cannot be cached here.
    return 'notAvailable';
};

const parseHashes = (raw: string): string[] =>
    raw
        .split(/[\s,;]+/)
        .map((h) => h.trim())
        .filter((h) => h.length > 0);

const NOT_APPLICABLE = '---';

const formatDate = (iso?: string): string => {
    if (!iso) {
        return NOT_APPLICABLE;
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return NOT_APPLICABLE;
    }
    // ISO yyyy-mm-dd is short and unambiguous for an admin UI.
    return d.toISOString().slice(0, 10);
};

// Derives the storage account name from a blob endpoint URI. Example:
// "https://srmsasovmockpripl.blob.core.windows.net/..." -> "srmsasovmockpripl".
// In default mode the backend may return several URIs comma-joined into one string
// (one per cloud the hash was found in), so we split, parse each, and rejoin
// the unique account names. Returns NOT_APPLICABLE when the input is missing or
// no segment yields a valid account name.
const extractStorageAccountName = (uri?: string): string => {
    if (!uri) {
        return NOT_APPLICABLE;
    }
    const segments = uri
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    if (segments.length === 0) {
        return NOT_APPLICABLE;
    }
    const names: string[] = [];
    for (const segment of segments) {
        try {
            const host = new URL(segment).hostname;
            const firstLabel = host.split('.')[0];
            if (firstLabel && !names.includes(firstLabel)) {
                names.push(firstLabel);
            }
        } catch {
            // Ignore unparseable segments; fall through to NOT_APPLICABLE if none parse.
        }
    }
    return names.length > 0 ? names.join(', ') : NOT_APPLICABLE;
};

type SortDirection = 'asc' | 'desc';

/**
 * Builds the post-transfer summary message.
 *
 * A transfer fans out to every available destination container, so the backend
 * returns one result row per (hash x destination container) pair. Counting rows
 * alone would report "12" for 3 blobs sent to 4 containers, so the breakdown is
 * shown explicitly whenever more than one container was targeted.
 */
const renderTransferSummary = (results: BlobTransferResult[]): string => {
    const succeeded = results.filter((r) => r.transferred).length;
    const blobCount = new Set(results.map((r) => r.hash)).size;
    const containerCount = new Set(
        results
            .map((r) => r.destinationContainerName)
            .filter((c): c is string => !!c)
    ).size;

    const plural = (count: number, singular: string, pluralForm: string) =>
        `${count} ${count === 1 ? singular : pluralForm}`;

    if (containerCount > 1) {
        return (
            `Transferred ${succeeded} of ${plural(
                results.length,
                'copy',
                'copies'
            )}` +
            ` (${plural(blobCount, 'blob', 'blobs')} to ${plural(
                containerCount,
                'container',
                'containers'
            )}).`
        );
    }

    return `Transferred ${succeeded} of ${plural(
        results.length,
        'blob',
        'blobs'
    )}.`;
};

// Per-column accessors that return the comparable value for sorting.
// Strings are lower-cased for case-insensitive ordering; dates are converted
// to epoch ms so they sort chronologically rather than lexicographically.
const sortValueAccessors: Record<
    string,
    (item: StorageBlobInfo) => string | number | undefined
> = {
    hash: (i) => i.hash?.toLowerCase(),
    exists: (i) => (i.exists ? 1 : 0),
    storageAccountName: (i) =>
        extractStorageAccountName(i.storageAccountUri).toLowerCase(),
    containerName: (i) => i.containerName?.toLowerCase(),
    createdOn: (i) =>
        i.createdOn ? new Date(i.createdOn).getTime() : undefined,
    expiresOn: (i) =>
        i.expiresOn ? new Date(i.expiresOn).getTime() : undefined,
    existsInCache: (i) =>
        i.existsInCache === undefined || i.existsInCache === null
            ? undefined
            : i.existsInCache
            ? 1
            : 0,
};

// Returns a new array sorted by the given column. Items with no value for the
// column (undefined/null/empty) are pushed to the end regardless of direction
// so non-existing-blob stub rows don't shuffle to the top on a date sort.
const sortItems = (
    items: StorageBlobInfo[],
    key: string,
    direction: SortDirection
): StorageBlobInfo[] => {
    const accessor = sortValueAccessors[key];
    if (!accessor) {
        return items;
    }
    const dir = direction === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        const aMissing = av === undefined || av === null || av === '';
        const bMissing = bv === undefined || bv === null || bv === '';
        if (aMissing && bMissing) {
            return 0;
        }
        if (aMissing) {
            return 1;
        }
        if (bMissing) {
            return -1;
        }
        if (av! < bv!) {
            return -dir;
        }
        if (av! > bv!) {
            return dir;
        }
        return 0;
    });
};

// Results are paged in the display grid to keep the DOM light on broad prefix
// searches; the transfer window below always operates on the full result set.
const RESULTS_PAGE_SIZE = 20;

const baseResultColumns: IColumn[] = [
    {
        key: 'hash',
        name: 'Hash',
        fieldName: 'hash',
        minWidth: 140,
        maxWidth: 280,
        isResizable: true,
    },
    {
        key: 'exists',
        name: 'Exists',
        fieldName: 'exists',
        minWidth: 60,
        maxWidth: 80,
        isResizable: false,
        onRender: (item: StorageBlobInfo) => (item.exists ? 'true' : 'false'),
    },
    {
        key: 'storageAccountName',
        name: 'Storage account',
        fieldName: 'storageAccountUri',
        minWidth: 160,
        maxWidth: 320,
        isResizable: true,
        // Default-mode rows may contain multiple comma-joined account names; allow wrapping.
        isMultiline: true,
        onRender: (item: StorageBlobInfo) =>
            item.exists
                ? extractStorageAccountName(item.storageAccountUri)
                : NOT_APPLICABLE,
    },
    {
        key: 'containerName',
        name: 'Container',
        fieldName: 'containerName',
        minWidth: 200,
        maxWidth: 480,
        isResizable: true,
        // Default-mode rows can list many containers comma-joined into one cell. Wrap so the
        // full list is visible instead of getting clipped with an ellipsis.
        isMultiline: true,
        onRender: (item: StorageBlobInfo) =>
            item.exists ? item.containerName ?? NOT_APPLICABLE : NOT_APPLICABLE,
    },
    {
        key: 'createdOn',
        name: 'Created on',
        fieldName: 'createdOn',
        minWidth: 110,
        maxWidth: 140,
        isResizable: true,
        onRender: (item: StorageBlobInfo) =>
            item.exists ? formatDate(item.createdOn) : NOT_APPLICABLE,
    },
    {
        key: 'expiresOn',
        name: 'Expires on',
        fieldName: 'expiresOn',
        minWidth: 110,
        maxWidth: 140,
        isResizable: true,
        onRender: (item: StorageBlobInfo) =>
            item.exists ? formatDate(item.expiresOn) : NOT_APPLICABLE,
    },
    {
        key: 'existsInCache',
        name: 'Cached?',
        fieldName: 'existsInCache',
        minWidth: 80,
        maxWidth: 110,
        isResizable: false,
        // Backend leaves this null in environments without a cache account
        // (anything other than dev / prodmock / prod), so surface that as "---".
        onRender: (item: StorageBlobInfo) => {
            if (
                item.existsInCache === undefined ||
                item.existsInCache === null
            ) {
                return NOT_APPLICABLE;
            }
            return item.existsInCache ? 'true' : 'false';
        },
    },
];

export const CheckHashInStorage: React.FC<ICheckHashInStorageProps> = (
    props: ICheckHashInStorageProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [hashesInput, setHashesInput] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [matches, setMatches] = useState<StorageBlobInfo[] | null>(null);
    const [requestedHashCount, setRequestedHashCount] = useState<number>(0);
    const [sortKey, setSortKey] = useState<string | undefined>(undefined);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [page, setPage] = useState<number>(1);

    // Transfer controls: which source the operator picked, the set of selected
    // (eligible) hashes, and the state of an in-flight transfer request.
    const [transferSource, setTransferSource] =
        useState<TransferSource>('cache');
    const [selectedHashes, setSelectedHashes] = useState<Set<string>>(
        () => new Set<string>()
    );
    const [isTransferring, setIsTransferring] = useState<boolean>(false);
    const [transferError, setTransferError] = useState<string | null>(null);
    const [transferResults, setTransferResults] = useState<
        BlobTransferResult[] | null
    >(null);

    // Holds the axios CancelTokenSource for the in-flight request so the user
    // can abort it via the Cancel button. Cleared on completion or unmount.
    const cancelSourceRef = useRef<CancelTokenSource | null>(null);

    useEffect(() => {
        return () => {
            cancelSourceRef.current?.cancel('Component unmounted');
            cancelSourceRef.current = null;
        };
    }, []);

    const stackTokens = { childrenGap: 16 };

    const hashes = useMemo(() => parseHashes(hashesInput), [hashesInput]);

    const handleHashesChange = (
        _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        setHashesInput(newValue ?? '');
    };

    const handleCheckClick = async () => {
        if (hashes.length === 0) {
            return;
        }

        // Defensive: cancel any prior in-flight request before starting a new one.
        cancelSourceRef.current?.cancel('Superseded by new request');
        const source = axios.CancelToken.source();
        cancelSourceRef.current = source;

        setIsLoading(true);
        setError(null);
        setMatches(null);
        setRequestedHashCount(hashes.length);

        // A fresh check invalidates any prior selection / transfer outcome.
        setSelectedHashes(new Set<string>());
        setTransferResults(null);
        setTransferError(null);

        try {
            // Always defer to the server's local-cloud default: it probes every
            // transfer container available in the current environment (one container
            // in single-cloud envs, all of them in dev/prodmock/prod).
            const response = await checkHashInStorage(
                hashes,
                undefined,
                source.token
            );

            if (response.succeeded) {
                setMatches(response.data ?? []);
            } else {
                setError(
                    response.message ||
                        response.exception ||
                        'Storage hash check failed.'
                );
            }
        } catch (err) {
            if (axios.isCancel(err)) {
                setError('Request cancelled.');
            } else {
                const e = err as {
                    response?: { data?: { message?: string } };
                    message?: string;
                };
                setError(
                    e.response?.data?.message ||
                        e.message ||
                        'Failed to call CheckHashInStorage endpoint.'
                );
            }
        } finally {
            // Only clear the ref if it still points at this request (a cancel
            // followed by a fresh start would have already replaced it).
            if (cancelSourceRef.current === source) {
                cancelSourceRef.current = null;
            }
            setIsLoading(false);
        }
    };

    const handleCancelClick = () => {
        cancelSourceRef.current?.cancel('User cancelled');
        cancelSourceRef.current = null;
    };

    // Changing the source flips the eligibility rule, so any previously-selected
    // hashes may no longer be valid — clear the selection and any prior results.
    const handleSourceChange = (
        _event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ) => {
        if (!option) {
            return;
        }
        setTransferSource(option.key as TransferSource);
        setSelectedHashes(new Set<string>());
        setTransferResults(null);
        setTransferError(null);
    };

    const handleTransferClick = async () => {
        // Source=Cache: the operator hand-picks which cache blobs to push out, so
        //   we transfer exactly the checked rows.
        // Source=Cloud Containers: we don't require a manual selection — we compile
        //   every hash that exists in some container and replicate them to the cache.
        const hashesToTransfer =
            transferSource === 'cache'
                ? Array.from(selectedHashes)
                : eligibleHashes;

        if (hashesToTransfer.length === 0) {
            return;
        }

        // Source=Cache  -> push cache blobs out to the transfer container (CacheToContainer).
        // Source=Cloud  -> pull container blobs back into the cache (ContainerToCache).
        const direction =
            transferSource === 'cache'
                ? TransferDirection.CacheToContainer
                : TransferDirection.ContainerToCache;

        setIsTransferring(true);
        setTransferError(null);
        setTransferResults(null);

        try {
            // overwrite = false. For a blob already present in the transfer container, the
            // handler intentionally does NOT re-copy it — the transfer containers are governed
            // by an Object Replication (ORS) policy, and a Copy Blob overwrite against an
            // ORS-managed blob is rejected with 409 OrsPolicyConflict. Instead the handler
            // touches the destination blob's metadata ("stamp") to emit a write event: this
            // re-triggers CTS to re-ship the blob onward and bumps Last-Modified, which is the
            // ORS-safe way to refresh an already-present blob.
            const response = await transferBlobs(
                hashesToTransfer,
                direction,
                WorkLane.Warm,
                false
            );

            setTransferResults(response.data ?? []);
            if (!response.succeeded) {
                setTransferError(
                    response.message ||
                        response.exception ||
                        'Blob transfer failed.'
                );
            }
        } catch (err) {
            const e = err as {
                response?: { data?: { message?: string } };
                message?: string;
            };
            setTransferError(
                e.response?.data?.message ||
                    e.message ||
                    'Failed to call TransferBlobs endpoint.'
            );
        } finally {
            setIsTransferring(false);
        }
    };

    const cardStyle: React.CSSProperties = {
        padding: '12px',
        backgroundColor: theme.palette.white,
        borderRadius: '4px',
        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
    };

    const foundCount = (matches ?? []).filter((m) => m.exists).length;

    // Toggle sort direction when clicking the active column; otherwise sort
    // ascending on the newly selected column.
    const handleColumnClick = (
        _ev?: React.MouseEvent<HTMLElement>,
        column?: IColumn
    ): void => {
        if (!column || !sortValueAccessors[column.key]) {
            return;
        }
        setSortKey((prevKey) => {
            if (prevKey === column.key) {
                setSortDirection((prevDir) =>
                    prevDir === 'asc' ? 'desc' : 'asc'
                );
                return prevKey;
            }
            setSortDirection('asc');
            return column.key;
        });
    };

    // Toggle a single row's selection. Only eligible rows are ever rendered with
    // a checkbox, so callers only reach this for eligible hashes.
    const toggleHashSelection = (
        hash: string | undefined,
        checked: boolean
    ) => {
        if (!hash) {
            return;
        }
        setSelectedHashes((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(hash);
            } else {
                next.delete(hash);
            }
            return next;
        });
    };

    // Rows the operator is allowed to select, given the current source. Drives
    // both the "select all" header checkbox and the transfer-button enablement.
    const eligibleHashes = useMemo<string[]>(
        () =>
            (matches ?? [])
                .filter((m) => !!m.hash && isRowEligible(m, transferSource))
                .map((m) => m.hash!),
        [matches, transferSource]
    );

    const allEligibleSelected =
        eligibleHashes.length > 0 &&
        eligibleHashes.every((h) => selectedHashes.has(h));

    const toggleSelectAll = (checked: boolean) => {
        setSelectedHashes(() =>
            checked ? new Set(eligibleHashes) : new Set<string>()
        );
    };

    // Prepend a selection column. Behavior depends on the chosen source:
    //  - Source = Cache: eligible rows (exist in cache) get an interactive checkbox
    //    so the operator hand-picks which blobs to push out; the header has a
    //    "select all eligible" checkbox.
    //  - Source = Cloud Containers: selection is implicit — every container blob
    //    not already cached is transferred — so we render a non-interactive
    //    three-state status icon and drop the header checkbox.
    const selectColumn = useMemo<IColumn>(
        () => ({
            key: 'select',
            name: '',
            minWidth: 36,
            maxWidth: 44,
            isResizable: false,
            onRenderHeader: () =>
                transferSource === 'cache' ? (
                    <Checkbox
                        ariaLabel="Select all eligible rows"
                        checked={allEligibleSelected}
                        disabled={eligibleHashes.length === 0}
                        onChange={(_e, checked) => toggleSelectAll(!!checked)}
                    />
                ) : null,
            onRender: (item: StorageBlobInfo) => {
                // Cloud Containers -> Cache: non-interactive three-state status.
                if (transferSource !== 'cache') {
                    const status = getCloudToCacheStatus(item);
                    if (status === 'willCache') {
                        return (
                            <Icon
                                iconName="SkypeCircleCheck"
                                aria-label="Will be added to cache"
                                title="Will be added to cache"
                                styles={{
                                    root: {
                                        color: theme.semanticColors.successIcon,
                                        fontSize: 16,
                                    },
                                }}
                            />
                        );
                    }
                    if (status === 'alreadyCached') {
                        return (
                            <Icon
                                iconName="CheckMark"
                                aria-label="Already cached; will be skipped"
                                title="Already cached; will be skipped"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralTertiary,
                                        fontSize: 16,
                                    },
                                }}
                            />
                        );
                    }
                    return (
                        <Icon
                            iconName="StatusErrorFull"
                            aria-label="Not present in any container; cannot transfer"
                            title="Not present in any container; cannot transfer"
                            styles={{
                                root: {
                                    color: theme.semanticColors.errorIcon,
                                    fontSize: 16,
                                },
                            }}
                        />
                    );
                }

                // Cache -> Cloud Containers: interactive checkbox for pushable rows,
                // red ✗ for rows not present in cache.
                if (!isRowEligible(item, transferSource)) {
                    return (
                        <Icon
                            iconName="StatusErrorFull"
                            aria-label="Not present in cache; cannot transfer"
                            title="Not present in cache; cannot transfer"
                            styles={{
                                root: {
                                    color: theme.semanticColors.errorIcon,
                                    fontSize: 16,
                                },
                            }}
                        />
                    );
                }

                return (
                    <Checkbox
                        ariaLabel={`Select ${item.hash}`}
                        checked={!!item.hash && selectedHashes.has(item.hash)}
                        onChange={(_e, checked) =>
                            toggleHashSelection(item.hash, !!checked)
                        }
                    />
                );
            },
        }),
        [
            transferSource,
            selectedHashes,
            allEligibleSelected,
            eligibleHashes,
            theme,
        ]
    );

    // Decorate the static column definitions with sort indicators and the
    // click handler so the DetailsList headers become interactive.
    const sortedColumns = useMemo<IColumn[]>(
        () => [
            selectColumn,
            ...baseResultColumns.map((col) => {
                const isSortable = !!sortValueAccessors[col.key];
                return {
                    ...col,
                    isSorted: isSortable && sortKey === col.key,
                    isSortedDescending:
                        isSortable &&
                        sortKey === col.key &&
                        sortDirection === 'desc',
                    onColumnClick: isSortable ? handleColumnClick : undefined,
                };
            }),
        ],
        [sortKey, sortDirection, selectColumn]
    );

    const sortedMatches = useMemo<StorageBlobInfo[]>(() => {
        if (!matches) {
            return [];
        }
        if (!sortKey) {
            return matches;
        }
        return sortItems(matches, sortKey, sortDirection);
    }, [matches, sortKey, sortDirection]);

    const totalPages = Math.max(
        1,
        Math.ceil(sortedMatches.length / RESULTS_PAGE_SIZE)
    );

    // Reset to the first page whenever the result set or sort changes so the
    // operator isn't stranded on an out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [matches, sortKey, sortDirection]);

    const pagedMatches = useMemo<StorageBlobInfo[]>(() => {
        const start = (page - 1) * RESULTS_PAGE_SIZE;
        return sortedMatches.slice(start, start + RESULTS_PAGE_SIZE);
    }, [sortedMatches, page]);

    return (
        <Stack tokens={stackTokens}>
            <MessageBar
                messageBarType={MessageBarType.info}
                styles={{
                    root: { backgroundColor: theme.palette.themeLighter },
                }}
            >
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    Probes the configured transfer storage account(s) for blobs
                    at <code>Builds/&#123;hash&#125;</code>, and (where
                    available) the hydration cache for blobs at{' '}
                    <code>&#123;hash&#125;</code>.
                </Text>
                <Text variant="small">
                    Checks every transfer container available in the current
                    environment. In single-cloud environments (e.g.{' '}
                    <em>USSec</em>, <em>Fairfax</em>) that's the cloud's own
                    container; in multi-cloud environments (<em>dev</em>,{' '}
                    <em>prodmock</em>, <em>prod</em>) every transfer container
                    is probed and matches are collapsed into one row per hash
                    with comma-joined locations.
                </Text>
            </MessageBar>

            <div style={cardStyle}>
                <TextField
                    label="Hashes"
                    placeholder="Paste one hash per line (whitespace, commas, or semicolons also accepted)"
                    multiline
                    rows={6}
                    autoAdjustHeight
                    value={hashesInput}
                    onChange={handleHashesChange}
                    description={
                        hashes.length > 0
                            ? `${hashes.length} hash${
                                  hashes.length === 1 ? '' : 'es'
                              } to check`
                            : 'Enter at least one hash to enable the check.'
                    }
                    borderless
                />
            </div>

            <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 12 }}
            >
                <PrimaryButton
                    text="Check Hashes"
                    iconProps={{ iconName: 'Search' }}
                    onClick={handleCheckClick}
                    disabled={isLoading || hashes.length === 0}
                />
                {isLoading && (
                    <DefaultButton
                        text="Cancel"
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={handleCancelClick}
                    />
                )}
                {isLoading && (
                    <Spinner
                        size={SpinnerSize.small}
                        label="Checking storage accounts…"
                        labelPosition="right"
                        ariaLive="assertive"
                    />
                )}
            </Stack>

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {error}
                </MessageBar>
            )}

            {!error && matches && (
                <>
                    <MessageBar
                        messageBarType={
                            foundCount > 0
                                ? MessageBarType.success
                                : MessageBarType.warning
                        }
                    >
                        Found {foundCount} match
                        {foundCount === 1 ? '' : 'es'} for {requestedHashCount}{' '}
                        requested hash
                        {requestedHashCount === 1 ? '' : 'es'}.
                    </MessageBar>

                    {matches.length > 0 && (
                        <div style={cardStyle}>
                            <DetailsList
                                items={pagedMatches}
                                columns={sortedColumns}
                                selectionMode={SelectionMode.none}
                                layoutMode={DetailsListLayoutMode.justified}
                                isHeaderVisible
                            />
                            {sortedMatches.length > RESULTS_PAGE_SIZE && (
                                <Stack
                                    horizontal
                                    verticalAlign="center"
                                    horizontalAlign="end"
                                    tokens={{ childrenGap: 12 }}
                                    style={{ marginTop: 8 }}
                                >
                                    <DefaultButton
                                        text="Previous"
                                        iconProps={{ iconName: 'ChevronLeft' }}
                                        onClick={() =>
                                            setPage((p) => Math.max(1, p - 1))
                                        }
                                        disabled={page <= 1}
                                    />
                                    <Text variant="small">
                                        Page {page} of {totalPages} ({' '}
                                        {sortedMatches.length} results)
                                    </Text>
                                    <DefaultButton
                                        text="Next"
                                        iconProps={{ iconName: 'ChevronRight' }}
                                        onClick={() =>
                                            setPage((p) =>
                                                Math.min(totalPages, p + 1)
                                            )
                                        }
                                        disabled={page >= totalPages}
                                    />
                                </Stack>
                            )}
                        </div>
                    )}

                    {matches.length > 0 && (
                        <div style={cardStyle}>
                            <Stack
                                horizontal
                                verticalAlign="end"
                                tokens={{ childrenGap: 16 }}
                                wrap
                            >
                                <Dropdown
                                    label="Source"
                                    selectedKey={transferSource}
                                    onChange={handleSourceChange}
                                    options={[
                                        {
                                            key: 'cache',
                                            text: 'Cache',
                                        },
                                        {
                                            key: 'cloudContainers',
                                            text: 'Cloud Containers',
                                        },
                                    ]}
                                    styles={{ root: { minWidth: 180 } }}
                                />

                                <Text
                                    variant="medium"
                                    styles={{
                                        root: {
                                            paddingBottom: 6,
                                            fontWeight: 600,
                                        },
                                    }}
                                >
                                    transfer to
                                </Text>

                                <Dropdown
                                    label="Destination"
                                    // Destination auto-resolves to the other side and is not
                                    // directly editable (blurred/disabled) — it mirrors Source.
                                    selectedKey={
                                        transferSource === 'cache'
                                            ? 'cloudContainers'
                                            : 'cache'
                                    }
                                    disabled
                                    options={[
                                        {
                                            key: 'cache',
                                            text: 'Cache',
                                        },
                                        {
                                            key: 'cloudContainers',
                                            text: 'Cloud Containers',
                                        },
                                    ]}
                                    styles={{ root: { minWidth: 180 } }}
                                />

                                <PrimaryButton
                                    text="Transfer"
                                    iconProps={{ iconName: 'Copy' }}
                                    onClick={handleTransferClick}
                                    disabled={
                                        isTransferring ||
                                        (transferSource === 'cache'
                                            ? selectedHashes.size === 0
                                            : eligibleHashes.length === 0)
                                    }
                                />

                                {isTransferring && (
                                    <Spinner
                                        size={SpinnerSize.small}
                                        label="Transferring…"
                                        labelPosition="right"
                                        ariaLive="assertive"
                                    />
                                )}

                                <Text
                                    variant="small"
                                    styles={{ root: { paddingBottom: 6 } }}
                                >
                                    {transferSource === 'cache'
                                        ? `${selectedHashes.size} selected`
                                        : `${eligibleHashes.length} will be added to cache`}
                                </Text>
                            </Stack>
                        </div>
                    )}

                    {transferError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {transferError}
                        </MessageBar>
                    )}

                    {!transferError && transferResults && (
                        <MessageBar
                            messageBarType={
                                transferResults.every((r) => r.transferred)
                                    ? MessageBarType.success
                                    : MessageBarType.warning
                            }
                        >
                            {renderTransferSummary(transferResults)}
                        </MessageBar>
                    )}
                </>
            )}
        </Stack>
    );
};
